"use client";

import { useId, useState } from "react";
import {
    Check,
    ChevronDown,
    GripVertical,
    List,
    Plus,
    SlidersHorizontal,
    Trash2,
} from "lucide-react";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import {
    KnowledgeCheckBlock,
    QuizAppearance,
    QuizOption,
    QuizQuestion,
    QuizQuestionType,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useResolvedGridGap } from "../../hooks/useGridGap";
import { useLomdaLocale } from "../../hooks/useLomdaLocale";
import {
    resolveQuizAppearance,
    createChoiceOptions,
    createQuizOption,
    createQuizQuestion,
    quizType,
} from "../../lib/blockRegistry";
import ColorSwatchField from "../colorSwatchField";
import {
    AppearanceReset,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];
/** Option text is rich (HTML); show it as plain text in the rename field. */
const stripHtml = (s: string) => s.replace(/<[^>]*>/g, "");
const Divider = () => <span className="h-6 w-px shrink-0 bg-border" />;

const TYPE_OPTIONS: { value: QuizQuestionType; labelKey: string }[] = [
    { value: "single", labelKey: "lomda-v3-editor.quiz.type-single" },
    { value: "multiple", labelKey: "lomda-v3-editor.quiz.type-multiple" },
    { value: "true-false", labelKey: "lomda-v3-editor.quiz.type-true-false" },
    { value: "open", labelKey: "lomda-v3-editor.quiz.type-open" },
];

export default function KnowledgeCheckSettings({
    block,
}: {
    block: KnowledgeCheckBlock;
}) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const focusedQuestionId = useLomdaV3Editor((s) => s.focusedQuestionId);
    const setBlockGridGap = useLomdaV3Editor((s) => s.setBlockGridGap);
    const gridGap = useResolvedGridGap(block.id);
    const { dir } = useLocale();
    const lomdaLocale = useLomdaLocale();
    const showInstructionId = useId();

    const { questions } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveQuizAppearance(block.data.appearance, theme);
    const update = (patch: Partial<KnowledgeCheckBlock["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setQuestions = (next: QuizQuestion[]) => update({ questions: next });
    const patchQuestion = (id: string, patch: Partial<QuizQuestion>) =>
        setQuestions(questions.map((q) => (q.id === id ? { ...q, ...patch } : q)));

    const focused = questions.find((q) => q.id === focusedQuestionId) ?? null;
    const type = focused ? quizType(focused) : null;

    const setType = (newType: QuizQuestionType) => {
        if (!focused) return;
        const patch: Partial<QuizQuestion> = {
            type: newType,
            multiple: newType === "multiple",
        };
        if (newType === "true-false") {
            patch.options = [
                { ...createQuizOption(), text: t("lomda-v3-editor.quiz.true", lomdaLocale), correct: true },
                { ...createQuizOption(), text: t("lomda-v3-editor.quiz.false", lomdaLocale) },
            ];
        } else if (newType === "single" || newType === "multiple") {
            // Coming from true-false / open → restore editable options.
            if (focused.type === "true-false" || focused.options.length < 2) {
                patch.options = createChoiceOptions();
            } else if (newType === "single") {
                // Keep only the first correct option.
                let kept = false;
                patch.options = focused.options.map((o) => {
                    if (o.correct && !kept) {
                        kept = true;
                        return o;
                    }
                    return { ...o, correct: false };
                });
            }
        }
        patchQuestion(focused.id, patch);
    };

    const toggleCorrect = (optId: string) => {
        if (!focused) return;
        if (type === "multiple") {
            patchQuestion(focused.id, {
                options: focused.options.map((o) =>
                    o.id === optId ? { ...o, correct: !o.correct } : o
                ),
            });
        } else {
            patchQuestion(focused.id, {
                options: focused.options.map((o) => ({
                    ...o,
                    correct: o.id === optId,
                })),
            });
        }
    };

    const isChoice = type === "single" || type === "multiple";

    return (
        <div className="flex items-center gap-3">
            {/* ── Block settings ── */}
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-sm !font-semibold text-foreground">
                    {t("lomda-v3-editor.interactive.knowledge-check")}
                </span>
                <WidthControl blockId={block.id} />

                <AppearancePopover
                    appearance={appearance}
                    onChange={(patch) =>
                        update({ appearance: { ...appearance, ...patch } })
                    }
                    onReset={() => update({ appearance: undefined })}
                />

                <button
                    type="button"
                    onClick={() =>
                        setQuestions([...questions, createQuizQuestion()])
                    }
                    className={chromeBtn}
                >
                    <Plus className="size-5" />
                    {t("lomda-v3-editor.quiz.add-question")}
                </button>

                <div className="flex shrink-0 items-center gap-2">
                    <span className="whitespace-nowrap text-sm text-foreground">
                        {t("lomda-v3-editor.settings.grid-gap")}
                    </span>
                    <div className="flex h-9 w-16 items-center rounded-lg border px-2 text-sm text-foreground">
                        <input
                            type="number"
                            min={0}
                            value={gridGap}
                            onChange={(e) =>
                                setBlockGridGap(
                                    block.id,
                                    Math.max(0, parseInt(e.target.value, 10) || 0)
                                )
                            }
                            className="w-full bg-transparent text-center outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* ── Focused question settings ── */}
            {focused && type && (
                <>
                    <Divider />
                    <div className="flex items-center gap-3">
                        {/* Type */}
                        <DropdownMenu dir={dir}>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className={cn(chromeBtn, chromeBtnOpen, "min-w-[150px] justify-between whitespace-nowrap")}
                                >
                                    {t(
                                        TYPE_OPTIONS.find((o) => o.value === type)
                                            ?.labelKey ?? ""
                                    )}
                                    <ChevronDown className="size-4 text-muted-foreground" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="min-w-[150px]">
                                <DropdownMenuRadioGroup
                                    value={type ?? ""}
                                    onValueChange={(v) =>
                                        setType(v as QuizQuestionType)
                                    }
                                >
                                    {TYPE_OPTIONS.map((o) => (
                                        <DropdownMenuRadioItem
                                            key={o.value}
                                            value={o.value}
                                        >
                                            {t(o.labelKey)}
                                        </DropdownMenuRadioItem>
                                    ))}
                                </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Correct answer (choice + true/false) */}
                        {type !== "open" && (
                            <CorrectAnswerControl
                                question={focused}
                                multiple={type === "multiple"}
                                onToggle={toggleCorrect}
                                dir={dir}
                            />
                        )}

                        {/* Manage options (choice only) — add lives inside it */}
                        {isChoice && (
                            <>
                                <ManageOptionsPopover
                                    question={focused}
                                    onChange={(options) =>
                                        patchQuestion(focused.id, { options })
                                    }
                                />
                            </>
                        )}

                        <div className="flex h-9 shrink-0 items-center gap-2">
                            <Label
                                htmlFor={showInstructionId}
                                className="whitespace-nowrap font-normal text-foreground"
                            >
                                {t("lomda-v3-editor.quiz.show-instruction")}
                            </Label>
                            <Switch
                                id={showInstructionId}
                                checked={!focused.hideInstruction}
                                onCheckedChange={(on) =>
                                    patchQuestion(focused.id, {
                                        hideInstruction: on ? undefined : true,
                                    })
                                }
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function CorrectAnswerControl({
    question,
    multiple,
    onToggle,
    dir,
}: {
    question: QuizQuestion;
    multiple: boolean;
    onToggle: (optId: string) => void;
    dir: "rtl" | "ltr";
}) {
    const correctLetters = question.options
        .map((o, i) => (o.correct ? LETTERS[i] ?? i + 1 : null))
        .filter(Boolean)
        .join(", ");
    return (
        <DropdownMenu dir={dir}>
            <DropdownMenuTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen, "justify-between")}>
                    {t("lomda-v3-editor.quiz.correct-answer")}
                    {correctLetters ? `: ${correctLetters}` : ""}
                    <ChevronDown className="size-4 text-muted-foreground" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[183px]">
                {question.options.map((o, i) => (
                    <DropdownMenuItem
                        key={o.id}
                        onSelect={(e) => {
                            if (multiple) e.preventDefault();
                            onToggle(o.id);
                        }}
                        className="flex items-center justify-between"
                    >
                        {t("lomda-v3-editor.quiz.option-n", undefined, {
                            letter: LETTERS[i] ?? i + 1,
                        })}
                        {o.correct && <Check className="size-4" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function ManageOptionsPopover({
    question,
    onChange,
}: {
    question: QuizQuestion;
    onChange: (options: QuizOption[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const options = question.options;

    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...options];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    <List className="size-5" />
                    {t("lomda-v3-editor.quiz.manage-options")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[412px] rounded-2xl p-4"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.quiz.manage-options")}
                    </PopoverTitle>
                    <div className="flex flex-col gap-1">
                        {options.map((o, i) => (
                            <div
                                key={o.id}
                                draggable
                                onDragStart={() => setDragIndex(i)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                    if (dragIndex !== null) move(dragIndex, i);
                                    setDragIndex(null);
                                }}
                                className="flex items-center gap-2.5 rounded-lg px-1 py-1.5"
                            >
                                <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                                <span className="text-sm text-primary">
                                    {LETTERS[i] ?? i + 1}
                                </span>
                                <input
                                    value={stripHtml(o.text)}
                                    onChange={(e) =>
                                        onChange(
                                            options.map((x) =>
                                                x.id === o.id
                                                    ? { ...x, text: e.target.value }
                                                    : x
                                            )
                                        )
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.quiz.option-placeholder"
                                    )}
                                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                                />
                                {o.correct && (
                                    <span className="shrink-0 text-xs text-muted-foreground">
                                        {t("lomda-v3-editor.quiz.correct-badge")}
                                    </span>
                                )}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                        onChange(
                                            options.filter((x) => x.id !== o.id)
                                        )
                                    }
                                    className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                                >
                                    <Trash2 className="size-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                            onChange([...options, createQuizOption()])
                        }
                        className="h-10 gap-2 self-start rounded-lg bg-transparent px-3 text-sm hover:bg-accent"
                    >
                        <Plus className="size-5" />
                        {t("lomda-v3-editor.quiz.add-option")}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function AppearanceRow({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-foreground">{label}</span>
            <span className="flex items-center gap-2">{children}</span>
        </div>
    );
}

function AppearancePopover({
    appearance,
    onChange,
    onReset,
}: {
    appearance: QuizAppearance;
    onChange: (patch: Partial<QuizAppearance>) => void;
    onReset: () => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.quiz.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[300px] rounded-2xl p-4">
                <div className="flex flex-col gap-3">
                    <AppearanceRow
                        label={t("lomda-v3-editor.quiz.appearance-option-bg")}
                    >
                        <span className="text-xs text-muted-foreground">
                            {appearance.background.replace(/^#/, "").toUpperCase()}
                        </span>
                        <ColorSwatchField
                            value={appearance.background}
                            onChange={(c) => onChange({ background: c })}
                        />
                    </AppearanceRow>
                    <AppearanceRow
                        label={t("lomda-v3-editor.quiz.appearance-border")}
                    >
                        <span className="text-xs text-muted-foreground">
                            {appearance.border.replace(/^#/, "").toUpperCase()}
                        </span>
                        <ColorSwatchField
                            value={appearance.border}
                            onChange={(c) => onChange({ border: c })}
                        />
                    </AppearanceRow>
                    <AppearanceRow
                        label={t("lomda-v3-editor.quiz.appearance-border-width")}
                    >
                        <div className="flex h-8 w-16 items-center rounded-lg border px-2 text-xs">
                            <input
                                type="number"
                                min={0}
                                max={8}
                                value={appearance.borderWidth}
                                onChange={(e) =>
                                    onChange({
                                        borderWidth: Math.max(
                                            0,
                                            parseInt(e.target.value, 10) || 0
                                        ),
                                    })
                                }
                                className="w-full bg-transparent text-center outline-none"
                            />
                            <span className="text-muted-foreground">px</span>
                        </div>
                    </AppearanceRow>
                    <AppearanceReset
                        onReset={onReset}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
